import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { folderPath } from '../utils/workoutFolders';

export const FolderIcon = ({ open = false }) => (
  <svg className="wf-icon" viewBox="0 0 20 16" width="18" height="14" aria-hidden="true">
    {open ? (
      <path d="M1 3a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v1H5.5a2 2 0 0 0-1.9 1.4L1.2 14.6A2 2 0 0 1 1 13.7V3zm3.4 4.6A1 1 0 0 1 5.4 7H19a1 1 0 0 1 1 1.3l-2 6a1 1 0 0 1-1 .7H2.2z" />
    ) : (
      <path d="M1 3a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V3z" />
    )}
  </svg>
);

// Дерево папок слева от списка. Корень (id = null) — весь раздел.
// dnd приходит со страницы: она знает, что именно тащат и куда это можно бросить
const WorkoutFolderTree = ({ tree, currentId, onSelect, dnd }) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(() => new Set());

  // Путь к открытой папке всегда раскрыт: иначе по дереву не видно, где мы
  useEffect(() => {
    const path = folderPath(tree, currentId);
    if (path.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      path.forEach((folder) => next.add(folder.id));
      return next;
    });
  }, [tree, currentId]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const renderNode = (folder, depth) => {
    const id = folder ? folder.id : null;
    const kids = tree.children.get(id) || [];
    const isOpen = id === null || expanded.has(id);
    const active = id === currentId;

    return (
      <li key={id ?? 'root'} role="treeitem" aria-expanded={kids.length ? isOpen : undefined} aria-selected={active}>
        <div
          className={`wf-node ${active ? 'wf-node--active' : ''} ${dnd.target === id && dnd.canDrop(id) ? 'wf-node--drop' : ''}`}
          style={{ paddingLeft: 6 + depth * 16 }}
          draggable={id !== null}
          onDragStart={(event) => dnd.startFolder(event, id)}
          onDragEnd={dnd.end}
          {...dnd.dropProps(id)}
        >
          {id !== null && kids.length > 0 ? (
            <button
              type="button"
              className="wf-caret"
              onClick={() => toggle(id)}
              aria-label={isOpen ? t('workouts.folders.collapse') : t('workouts.folders.expand')}
            >
              <span className={isOpen ? 'wf-caret-icon wf-caret-icon--open' : 'wf-caret-icon'}>▸</span>
            </button>
          ) : (
            <span className="wf-caret wf-caret--empty" />
          )}
          <button type="button" className="wf-node-label" onClick={() => onSelect(id)}>
            <FolderIcon open={active} />
            <span className="wf-node-name" title={folder ? folder.name : undefined}>
              {folder ? folder.name : t('workouts.folders.root')}
            </span>
            <span className="wf-node-count">{id === null ? tree.rootTotal : tree.totals.get(id)}</span>
          </button>
        </div>
        {isOpen && kids.length > 0 && (
          <ul role="group">{kids.map((child) => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <ul className="wf-tree" role="tree" aria-label={t('workouts.folders.title')}>
      {renderNode(null, 0)}
    </ul>
  );
};

export default WorkoutFolderTree;
